import BodyType from 'owa-service/lib/contract/BodyType';
import { ContentPosition, InsertOption, PositionType } from 'roosterjs-editor-types';
import { Editor, EditorPlugin } from 'roosterjs-editor-core';
import { SelectedData } from 'owa-addins-core';

export default class AddinBodyEditorPlugin implements EditorPlugin {
    private editor: Editor;
    private plainTextEditor: HTMLTextAreaElement;

    public getName() {
        return 'AddIn';
    }

    public initialize(editor: Editor) {
        this.editor = editor;
    }

    public dispose() {
        this.editor = null;
        this.plainTextEditor = null;
    }

    private isPlainTextEditor(): boolean {
        return !!this.plainTextEditor;
    }

    public setPlainTextEditor(plainTextEditor: HTMLTextAreaElement) {
        this.plainTextEditor = plainTextEditor;
    }

    public updateContent(content: string, bodyType: BodyType) {
        if (this.editor.getContent(false /* will trigger ExtractContentEvent */) == content) {
            return;
        }
        this.editor.setContent('');
        let contentElement = this.createNodeFromContent(content);
        this.insertNode(contentElement, {
            position: ContentPosition.Begin,
            updateCursor: true,
            replaceSelection: false,
            insertOnNewLine: false,
        });
        this.editor.select(contentElement, PositionType.After);
        this.triggerContentChangedByAddinEvent();
        this.editor.addUndoSnapshot();
    }

    public getContent(): string {
        return this.isPlainTextEditor() ? this.plainTextEditor.value : this.editor.getContent();
    }

    public getSelectedData(): SelectedData {
        if (this.isPlainTextEditor()) {
            const start = this.plainTextEditor.selectionStart;
            const end = this.plainTextEditor.selectionEnd;
            const data = this.plainTextEditor.value.substring(start, end);
            return {
                data: data,
                sourceProperty: 'body',
                startPosition: start,
                endPosition: end,
            };
        } else {
            let currentActiveSelectionRange = this.editor.getSelectionRange();
            let clonedSelection = currentActiveSelectionRange.cloneContents();
            let div = document.createElement('div');
            div.appendChild(clonedSelection);
            return {
                data: div.innerHTML.length > 0 ? div.innerHTML : null,
                sourceProperty: 'body',
                startPosition: currentActiveSelectionRange.startOffset,
                endPosition: currentActiveSelectionRange.endOffset,
            };
        }
    }

    public prependContent(content: string) {
        if (this.isPlainTextEditor()) {
            this.plainTextEditor.value = content + this.plainTextEditor.value;
        } else {
            let contentElement = this.createNodeFromContent(content);
            this.insertNode(contentElement, {
                position: ContentPosition.Begin,
                updateCursor: false,
                replaceSelection: false,
                insertOnNewLine: false,
            });
        }
    }

    public setSelectedData(content: string) {
        if (this.isPlainTextEditor()) {
            const start = this.plainTextEditor.selectionStart;
            const end = this.plainTextEditor.selectionEnd;
            this.plainTextEditor.value =
                this.plainTextEditor.value.substring(0, start) +
                content +
                this.plainTextEditor.value.substring(end + 1);
        } else {
            let contentElement = this.createNodeFromContent(content);
            const contentPosition = this.editor.getSelectionRange()
                ? ContentPosition.SelectionStart
                : ContentPosition.End;
            this.insertNode(contentElement, {
                position: contentPosition,
                updateCursor: true,
                replaceSelection: true,
                insertOnNewLine: false,
            });
        }
    }

    private insertNode(contentElement: HTMLElement, options: InsertOption) {
        this.editor.addUndoSnapshot();
        this.editor.focus();
        this.triggerContentChangedByAddinEvent();
        this.editor.insertNode(contentElement, options);
        this.triggerContentChangedByAddinEvent();
        this.editor.addUndoSnapshot();
    }

    private createNodeFromContent(content: string): HTMLElement {
        let contentNode: HTMLElement = this.editor
            .getDocument()
            .createElement('span') as HTMLAnchorElement;
        contentNode.innerHTML = content;

        return contentNode;
    }

    public triggerContentChangedByAddinEvent() {
        this.editor.triggerContentChangedEvent('Addins');
    }
}
